/**
 * lbrgt-cart-popup
 * -----------------
 * Shows a centered "added to cart" confirmation modal, replacing the
 * native Enterprise cart drawer/redirect for the add-to-cart moment.
 *
 * Hooks into Enterprise's native `on:cart:add` and `on:line-item:change`
 * events (see Theme settings > Advanced > Custom HTML > JS Developer
 * Documentation). Does NOT perform its own /cart/add.js request - it
 * only reads the result back via /cart.js, to avoid duplicate add calls.
 *
 */
class LbrgtCartPopup extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector("[data-lbrgt-cart-popup-overlay]");
    this.itemsList = this.querySelector("[data-lbrgt-cart-popup-items]");
    this.itemTemplate = this.querySelector(
      "[data-lbrgt-cart-popup-item-template]",
    );
    this.viewCartBtn = this.querySelector("[data-lbrgt-cart-popup-view-cart]");

    /* Julius Start */
    // in connectedCallback(), replace the previous gift block with:
    this.giftAlert = this.querySelector("[data-lbrgt-cart-popup-gift-alert]");
    this.giftWidget = this.querySelector("[data-lbrgt-cart-popup-gift-widget]");
    this.giftSkipBtn = this.querySelector("[data-lbrgt-cart-popup-gift-skip]");
    this.giftDismissed = false;
    this.giftAlreadyInCart = false;

    this.giftSkipBtn?.addEventListener("click", () => {
      this.giftDismissed = true;
      this.giftWidget.hidden = true;      // hides the whole widget, as asked
      this.giftAlert.hidden = true;
      this.giftAlert.classList.remove("is-blinking");
      this.giftSkipBtn.hidden = true;
    });

    this.viewCartBtn?.addEventListener("click", this.guardViewCart.bind(this), true);
    /* Julius End */

    this._onCartAdd = this.handleCartAdd.bind(this);
    this._onLineItemChange = this.handleLineItemChange.bind(this);
    this._onKeydown = this.handleKeydown.bind(this);

    document.addEventListener("on:cart:add", this._onCartAdd);
    document.addEventListener("on:line-item:change", this._onLineItemChange);

    this.querySelectorAll("[data-lbrgt-cart-popup-close]").forEach((el) => {
      el.addEventListener("click", () => this.close());
    });
    this.overlay.addEventListener("click", () => this.close());

    // If cart type is "drawer", open the native drawer instead of
    // navigating away, so the customer stays on the current page.
    if (this.dataset.cartType === "drawer") {
      this.viewCartBtn.addEventListener("click", (e) => {
        e.preventDefault();
        this.close();
        document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:open"));
      });
    }
  }

  /* Julius Start */
  async setupGiftSection() {
    const cfg = window.freeGiftSettings;
    this.giftDismissed = false;
    if (!cfg?.enabled || !this.giftWidget) return;

    let hasGift = false;
    try {
      const cart = await fetch("/cart.js").then((r) => r.json());
      hasGift = cart.items.some((i) => cfg.variantIds?.includes(i.variant_id));
    } catch (err) {
      console.error("lbrgt-cart-popup: gift cart check failed", err);
    }

    this.giftAlreadyInCart = hasGift;
    this.giftWidget.hidden = hasGift;
    
    // call once in setupGiftSection(), after hasGift is determined and widget is unhidden
    if (!hasGift && this.giftWidget) {
      this.wireGiftAvailabilityMessage();
    }
    
    if (this.giftAlert) { this.giftAlert.hidden = true; this.giftAlert.classList.remove("is-blinking"); }
    if (this.giftSkipBtn) this.giftSkipBtn.hidden = hasGift;
  }

  wireGiftAvailabilityMessage() {
    const wrap = this.giftWidget.querySelector(".free-gift-widget__variants");
    const msg = this.giftWidget.querySelector(".free-gift-widget__msg");
    if (!wrap || !msg || wrap.dataset.msgWired) return;
    wrap.dataset.msgWired = "true"; // avoid double-binding if this popup instance reopens

    // Fix 2026-08-29: Der Hinweis stand fest auf Deutsch hier im Code, obwohl es
    // ihn uebersetzt gibt. Er kommt jetzt aus dem data-Attribut, das
    // lbrgt-cart-popup.liquid aus products.product.variant_unavailable fuellt.
    // Der Rueckfall greift nur, falls das Attribut fehlt (altes Markup im Cache).
    const textNichtVerfuegbar =
      this.dataset.textVariantUnavailable || "Diese Variante ist leider nicht verfügbar.";

    const selects = Array.from(wrap.querySelectorAll(".free-gift-widget__select"));
    const showMsg = (text) => {
      if (!text) { msg.hidden = true; msg.textContent = ""; return; }
      msg.hidden = false;
      msg.textContent = text;
      msg.style.color = "#666";
    };

    selects.forEach((s) => s.addEventListener("change", () => {
      const v = this.getGiftMatchedVariant();
      if (v && !v.available) showMsg(textNichtVerfuegbar);
      else showMsg(null);
    }));
  }

  getGiftMatchedVariant() {
    const wrap = this.giftWidget?.querySelector(".free-gift-widget__variants");
    const dataEl = this.giftWidget?.querySelector(".free-gift-widget__variants-data");
    if (!wrap || !dataEl) return null;

    let variants;
    try { variants = JSON.parse(dataEl.textContent); } catch { return null; }

    const selects = Array.from(wrap.querySelectorAll(".free-gift-widget__select"));
    const chosen = selects.map((s) => s.value);
    if (chosen.some((v) => v === "")) return null;

    return variants.find((v) => {
      const opts = [v.option1, v.option2, v.option3].filter(Boolean);
      return chosen.every((val, i) => opts[i] === val);
    }) || null;
  }

  async guardViewCart(e) {
    const cfg = window.freeGiftSettings;
    if (!cfg?.enabled || !cfg.hasVariants || this.giftAlreadyInCart || this.giftDismissed) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    const variant = this.getGiftMatchedVariant();
    if (!variant || !variant.available) {
      this.giftAlert.hidden = false;
      this.giftAlert.classList.remove("is-blinking");
      void this.giftAlert.offsetWidth;
      this.giftAlert.classList.add("is-blinking");
      return;
    }

    try {
      await fetch("/cart/add.js", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: 1, properties: { _free_gift: "true" } }] }),
      });
    } catch (err) {
      console.error("lbrgt-cart-popup: failed to add gift", err);
    }
    this.proceedToCart();
  }

  proceedToCart() {
    if (this.dataset.cartType === "drawer") {
      this.close();
      document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:open"));
    } else {
      window.location.href = this.viewCartBtn.href;
    }
  }
  /* Julius End */

  disconnectedCallback() {
    document.removeEventListener("on:cart:add", this._onCartAdd);
    document.removeEventListener("on:line-item:change", this._onLineItemChange);
    document.removeEventListener("keydown", this._onKeydown);
  }

  handleCartAdd(event) {
    this.showCartFromEvent(event.detail);
  }

  handleLineItemChange(event) {
    const detail = event.detail || {};
    if (detail.newQuantity == null || detail.oldQuantity == null) return;
    if (detail.newQuantity <= detail.oldQuantity) return;

    this.showCartFromEvent(detail);
  }

  async showCartFromEvent(detail) {
    detail = detail || {};
    let items = detail.cart?.items || detail.response?.items || null;
    if (!items) {
      try {
        const response = await fetch("/cart.js");
        if (!response.ok) throw new Error(`cart.js returned ${response.status}`);
        const cart = await response.json();
        items = cart.items;
      } catch (err) {
        console.error("lbrgt-cart-popup: failed to fetch /cart.js", err);
      }
    }

    if (!items || !items.length) {
      console.warn(detail);
      return;
    }

    /* Julius Start */
    this.renderItems(items);
    this.filterCareSlides(detail.variantId || items[0]?.variant_id);
    await this.setupGiftSection();
    this.open();
    /* Julius End */
  }

  /**
   * Pflegeartikel je Farbe: jeder Pflege-Slide traegt data-lbrgt-care-variants
   * (',id,id,' oder '*'). Nach dem Hinzufuegen nur die zur Variante passenden zeigen,
   * die uebrigen aus dem Slider nehmen (Swiper zaehlt versteckte Slides sonst mit).
   * @param {number|string} variantId
   */
  filterCareSlides(variantId) {
    const swiperEl = this.querySelector(".popup-recommended-swiper");
    const wrapper = swiperEl?.querySelector(".swiper-wrapper");
    if (!wrapper) return;
    if (!this._careSlides) {
      this._careSlides = Array.from(wrapper.querySelectorAll("[data-lbrgt-care-variants]"));
    }
    if (!this._careSlides.length) return;

    const needle = `,${variantId},`;
    this._careSlides.forEach((slide) => slide.remove());
    const passend = this._careSlides.filter((slide) => {
      const ids = slide.dataset.lbrgtCareVariants || "";
      return ids === "*" || (variantId && ids.includes(needle));
    });
    wrapper.prepend(...passend);
    if (swiperEl.swiper) {
      swiperEl.swiper.update();
      swiperEl.swiper.slideTo(0, 0);
    }
  }

  /**
   * Reads the size/color option-name lists off the nudge box rendered
   * inside this popup, so option matching stays correct across locales
   * (mirrors the same settings-driven lists used on the PDP and cart page).
   * @returns {{size: string[], color: string[]}}
   */
  getNudgeOptionNames() {
    const nudge = this.querySelector(".size-nudge-cta");
    const parseList = (raw) =>
      (raw || "")
        .split(",")
        .map((name) => name.trim())
        .filter(Boolean);

    return {
      size: parseList(nudge?.dataset.sizeOptionNames),
      color: parseList(nudge?.dataset.colorOptionNames),
    };
  }

  /**
   * Reads a specific option's value off a /cart.js line item, trying
   * every translated option name until one matches.
   * @param {Object} item - A /cart.js line item.
   * @param {string[]} optionNames - Translated names to match against.
   * @returns {?string}
   */
  getOptionValue(item, optionNames) {
    if (!item.options_with_values || !optionNames.length) return null;
    const match = item.options_with_values.find((opt) =>
      optionNames.includes(opt.name),
    );
    return match ? match.value : null;
  }

  renderItems(items) {
    this.itemsList.innerHTML = "";
    const { size: sizeOptionNames, color: colorOptionNames } =
      this.getNudgeOptionNames();

    items.forEach((item) => {
      const node = this.itemTemplate.content.cloneNode(true);
      const li = node.querySelector(".lbrgt-cart-popup__item");

      const qtyEl = node.querySelector("[data-item-qty]");
      const imageEl = node.querySelector("[data-item-image]");
      const titleEl = node.querySelector("[data-item-title]");
      const variantEl = node.querySelector("[data-item-variant]");
      const deliveryEl = node.querySelector("[data-item-delivery]");
      const priceEl = node.querySelector("[data-item-price]");

      qtyEl.textContent = item.quantity;
      imageEl.src = this.getItemImage(item);
      imageEl.alt = item.product_title || item.title || "";
      titleEl.textContent = item.product_title || item.title || "";
      variantEl.textContent = this.buildVariantLabel(item);
      deliveryEl.textContent = item.properties?.["Lieferzeit"] || "";
      priceEl.textContent = this.formatMoney(
        item.final_line_price ?? item.line_price ?? item.price,
      );

      // Tag each item so nudge-cta.js can compare line items against
      // each other, the same way it does for the cart page's rows.
      if (li) {
        li.classList.add("js-cart-nudge-item");
        li.dataset.productId = item.product_id;
        const size = this.getOptionValue(item, sizeOptionNames);
        const color = this.getOptionValue(item, colorOptionNames);
        if (size != null) li.dataset.itemSize = size;
        if (color != null) li.dataset.itemColor = color;
      }

      this.itemsList.appendChild(node); // stays last — appends the fully-populated clone
    });

    // Let nudge-cta.js know the popup's items are in the DOM and ready
    // to be checked for a size/color nudge.
    document.dispatchEvent(
      new CustomEvent("on:cart-popup:rendered", { detail: { root: this } }),
    );
  }

  /**
   * Baut die Varianten-Anzeige und rechnet numerische Groessenwerte in die
   * aktive Einheit um (UK-Standard). Das variant_title von /cart.js ist EU-
   * basiert; nicht-numerische Werte (Farbe) bleiben unveraendert. Respektiert
   * die aktive Einheit (SizeConversion.getType) - im EU-Modus bleibt alles EU.
   * @param {object} item - /cart.js line item
   * @returns {string}
   */
  buildVariantLabel(item) {
    const opts = item.options_with_values;
    if (!opts || !opts.length) return item.variant_title || "";
    const SC = window.SizeConversion;
    const uk = SC && typeof SC.getType === "function" && SC.getType() === "uk";
    const parts = opts.map((opt) => {
      const val = String(opt.value == null ? "" : opt.value).trim();
      if (uk && /^\d+(,\d+)?$/.test(val) && typeof SC.convert === "function") {
        const conv = SC.convert(val);
        return conv && conv.uk ? conv.uk : val;
      }
      return opt.value;
    });
    return parts.join(" / ");
  }

  getItemImage(item) {
    if (item.image) return item.image.replace(/(\.[^.]+)$/, "_128x128$1");
    if (item.featured_image?.url) return item.featured_image.url;
    return "";
  }

  formatMoney(cents) {
    if (cents == null) return "";
    if (window.Shopify?.formatMoney && window.theme?.moneyFormat) {
      return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
    }
    // Fallback formatter if the theme's money-format helper isn't
    // available in this scope - adjust locale/currency as needed.
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: window.Shopify?.currency?.active || "EUR",
    }).format(cents / 100);
  }
  open() {
    this.hidden = false;
    document.addEventListener("keydown", this._onKeydown);
    this.querySelector("[data-lbrgt-cart-popup-close]")?.focus();
    document.body.style.overflow = "hidden";
  }
  close() {
    this.hidden = true;
    document.removeEventListener("keydown", this._onKeydown);
    document.body.style.overflow = "";
  }
  handleKeydown(e) {
    if (e.key === "Escape") this.close();
  }
}
customElements.define("lbrgt-cart-popup", LbrgtCartPopup);

//recommended products slider
new Swiper(".popup-recommended-swiper", {
  slidesPerView: 2,
  spaceBetween: 16,
  navigation: {
    nextEl: ".popup-slider-next",
    prevEl: ".popup-slider-prev",
  },
  pagination: {
    el: ".popup-slider-pagination",
    clickable: true,
  },
  breakpoints: {
    1024: {
      slidesPerView: 3,
    },
  },
});